// @flow

import * as React from 'react';

import { useDispatch } from 'react-redux';

import {
  EditForm,
  actions,
  SubmitButton,
  type Template,
  type EditFormProps,
} from '@gandi/template2';
import { useTranslator } from '@gandi/translate';
import { Button, Base } from '@gandi/ui';

import { selectSetupOwner } from 'src/Cart/Setup/modules/selectors';
import type { CreateOrTransfer } from 'src/Cart/Setup/types';
import { useAppSelector } from 'src/hooks/useAppSelector';

import { getDefaultTemplateId } from '../modules/actions';

import Editor from './Editor';

export type Props = {
  onSubmitSuccess: $NonMaybeType<EditFormProps['onSubmitSuccess']>,
  onSubmitFail: EditFormProps['onSubmitFail'],
  selectedTemplate: Partial<Template>,
  process: CreateOrTransfer,
  backUrl: string,
};

const defaultFormAttr = {
  create: 'default_registration',
  transfer: 'default_transfer',
};

const Edit = ({
  onSubmitSuccess,
  onSubmitFail,
  selectedTemplate,
  process,
  backUrl,
}: Props): React.Node => {
  const { __ } = useTranslator();

  const dispatch = useDispatch();

  const dispatchGetDefaultId = () => dispatch(getDefaultTemplateId(process));

  const sharingId = useAppSelector(selectSetupOwner);

  const defaultAttr = defaultFormAttr[process];

  // fill the form with default
  const transformInitialValues: $NonMaybeType<
    EditFormProps['transformInitialValues'],
  > = async (formData, template) => {
    const defaultId = await dispatchGetDefaultId();

    // $FlowFixMe[incompatible-type] TODO: Fix type
    return {
      ...formData,
      [defaultAttr]: template.id === defaultId,
    };
  };

  const handleSubmit: Props['onSubmitSuccess'] = async ({
    template,
    formValues,
  }) => {
    const defaultId = await dispatchGetDefaultId();

    if (formValues[defaultAttr] && template.id !== defaultId) {
      // $FlowFixMe[incompatible-call]
      dispatch(
        actions.setDefaultTemplate({
          productType: 'domain',
          process,
          templateId: template.id,
          sharingId,
        }),
      );
    }

    if (!formValues[defaultAttr] && template.id === defaultId) {
      // $FlowFixMe[incompatible-call]
      dispatch(
        actions.deleteDefaultTemplate({
          productType: 'domain',
          process,
          sharingId,
        }),
      );
    }

    onSubmitSuccess({ template, formValues });
  };

  return (
    <EditForm
      transformInitialValues={transformInitialValues}
      onSubmitSuccess={handleSubmit}
      onSubmitFail={onSubmitFail}
      // $FlowFixMe[incompatible-type]
      uuid={selectedTemplate.id}
    >
      <Base marginBottom="base">
        <Editor process={process} />
      </Base>
      <Base marginBottom="base">
        <Button grouped kind="outline" to={backUrl}>
          {__('Cancel')}
        </Button>
        <SubmitButton grouped type="submit">
          {__('Apply')}
        </SubmitButton>
      </Base>
    </EditForm>
  );
};

export default Edit;
